/**
 * Voice AI — AbortableLoader
 * Centralizes AbortController lifecycle for cancellable data-fetching.
 *
 * Usage:
 *   this._loader = new AbortableLoader();
 *   const result = await this._loader.run(signal => service.loadData({...}, { signal }));
 *   if (result.aborted) return;
 *
 * Returns one of:
 *   { data }          — success
 *   { aborted: true } — request was superseded by a newer run() call
 *   { error }         — real error (network, API, session expiry)
 */

export class AbortableLoader {
  constructor() {
    this._controller = null;
  }

  /**
   * Run an async function with automatic abort management.
   * If a previous run is still in-flight, it is aborted first.
   * @param {function(AbortSignal): Promise} fn - receives signal, returns a promise
   * @returns {Promise<{data?: any, aborted?: boolean, error?: Error}>}
   */
  async run(fn) {
    if (this._controller) this._controller.abort();
    const controller = new AbortController();
    this._controller = controller;

    try {
      const data = await fn(controller.signal);
      if (controller.signal.aborted) return { aborted: true };
      return { data };
    } catch (err) {
      if (err.name === 'AbortError') return { aborted: true };
      return { error: err };
    }
  }

  /** Abort the current in-flight request (e.g., on component unmount). */
  abort() {
    if (this._controller) {
      this._controller.abort();
      this._controller = null;
    }
  }
}
